import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import Sidebar from "./sidebar";
import { Link as LinkIcon, Edit, ChevronDown  } from "react-feather";
import { Link } from "react-router-dom";
import api from "../api";
import InsightsDashboard from "./InsightsDashboard";
import CustomerSegmentsGrid from "./CustomerSegmentsGrid";
import DonutChart from "./DonutChart";
import RetargetPerformanceCard from "./RetargetPerformanceCard ";
import BarChart from "./BarChart";
import PieChart from './PieChart';
import SMSBulkPopup from "./SMSBulkPopup";
import useStoreSetupCheck from './useStoreSetupCheck';


function RetargetTask() {
  useStoreSetupCheck();
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const [cashRewards, setCashRewards] = useState([]);
  const [shopwebsite, setWebsite] = useState([]);
  const [reorderTasks, setReorderTasks] = useState([]);
  const [insights, setInsights] = useState([]);
  const [summary, setSummary] = useState({});
  const [segments, setSegments] = useState({});
  const [ocharts, setOcharts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const [showPopup, setShowPopup] = useState(false);
  const [popupTasks, setPopupTasks] = useState([]);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const token = localStorage.getItem("access_token");

    // ✅ PRIMARY: Load fast (core UI)
    api
      .get(baseUrl + "/vendor-reward-advice/", {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setCashRewards(res.data.cash_rewards || []);
        setReorderTasks(res.data.reorder || []);
        setSummary(res.data.summary || {});
        setInsights(res.data.insights || {});
        setSegments(res.data.segments || []);
        setOcharts(res.data.charts || []);
        setWebsite(res.data.website || {});
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false); // ✅ UI becomes interactive immediately
      });

      // ✅ SECONDARY: Load in background (non-blocking)
      setTimeout(() => {
        api
          .get(baseUrl + "/retarget-performance/", {
            headers: { Authorization: `Bearer ${token}` },
          })
          .then((res) => {
            setStats(res.data);
          })
          .catch(() => {
            setError("Failed to fetch performance data.");
          });
      }, 0); // 👈 pushes to next event loop (non-blocking)

    }, []);

  const charts = !loading && stats.charts;
  return (
    <>
      <Helmet>
        <title>Repeat Revenue Agent</title>
        <meta
          name="description"
          content={`Kadhr turns your sales data into quick, ready-to-send actions that keep customers coming back.`}
        />
      </Helmet>
      <section className="container mtopcon dashb">
        <div className="row">
          <div className="col-12 col-lg-2 dash-min mb-3">
            <div className="nav-left-sidebar sidebar-dark pc">
              <div className="slimScrollDiv">
                <div className="menu-list">
                  <nav className="navbar navbar-expand-lg navbar-light nav-l-con">
                    <div className="collapse navbar-collapse" id="navbarNav">
                      <Sidebar />
                    </div>
                  </nav>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-10 dash-max">

            <div className='row'> 
              <div className='col-lg-4 col-12 mb-4'> 
                    {/* SUMMARY CARDS */}
                    <div className="row containerzz mb-4" 
                        style={{ cursor: "pointer" }}
                        onClick={() => setOpen((prev) => !prev)}>

                      {/* HEADER (clickable) */}
                      <div
                        className="d-flex justify-content-between align-items-center mb-2">
                        <div className="d-flex align-items-center">
                          <h3 className="mb-0">Repeat Revenue</h3>
                          <span className="badge-retarget ml-2">Agent</span>
                        </div>

                        {/* Arrow */}
                        <ChevronDown
                          size={24}
                          style={{
                            transition: "0.2s",
                            transform: open ? "rotate(180deg)" : "rotate(0deg)",
                          }}
                        />
                      </div>

                      {/* SUBTEXT */}
                      <p className="font9">
                        Turn customer behavior into predictable repeat revenue.
                      </p>

                      {/* GRID (accordion content) */}
                      {open && (
                        <div className="grid pb-2">

                          {/* TOTAL REVENUE */}
                          <div className="card">
                            <div className='row w-100'>
                              <div className='col-12 pr-0 plpt'>
                                <h4 className="text font90x mb-0 text-white">Revenue</h4>
                              </div>
                              <div className='col-12'>
                                <h2 className="mb-1 text-white mb-0">
                                  {summary?.total_revenue
                                    ? Intl.NumberFormat().format(summary.total_revenue)
                                    : 0}
                                </h2>
                              </div>
                            </div>
                          </div>

                          {/* AVG ORDER VALUE */}
                          <div className="card">
                            <div className='row w-100'>
                              <div className='col-12 pr-0 plpt'>
                                <h4 className="text font90x mb-0 text-white">Avg Order</h4>
                              </div>
                              <div className='col-12'>
                                <h2 className="mb-1 text-white mb-0">
                                  {insights.kpis?.avg_order_value
                                    ? Intl.NumberFormat().format(insights.kpis.avg_order_value)
                                    : 0}
                                </h2>
                              </div>
                            </div>
                          </div>

                          {/* REPEAT RATE */}
                          <div className="card">
                            <div className='row w-100'>
                              <div className='col-12 pr-0 plpt'>
                                <h4 className="text font90x mb-0 text-white">Repeat Rate</h4>
                              </div>
                              <div className='col-12'>
                                <h2 className="mb-1 text-white mb-0">
                                  {insights.kpis?.repeat_purchase_rate
                                    ? `${(insights.kpis.repeat_purchase_rate * 100).toFixed(0)}%`
                                    : "0%"}
                                </h2>
                              </div>
                            </div>
                          </div>

                          {/* CHURN */}
                          <div className="card">
                            <div className='row w-100'>
                              <div className='col-12 pr-0 plpt'>
                                <h4 className="text font90x mb-0 text-white">Churn</h4>
                              </div>
                              <div className='col-12'>
                                <h2 className="mb-1 text-white mb-0">
                                  {insights.kpis?.churn_rate
                                    ? `${(insights.kpis.churn_rate * 100).toFixed(0)}%`
                                    : "0%"}
                                </h2>
                              </div>
                            </div>
                          </div>

                          {/* REORDER */}
                          <div className="card">
                            <div className='row w-100'>
                              <div className='col-12 pr-0 plpt'>
                                <h4 className="text font90x mb-0 text-white">Reorder Cycle</h4>
                              </div>
                              <div className='col-12'>
                                <h2 className="mb-1 text-white mb-0">
                                  {insights.timing_insights?.avg_reorder_days
                                    ? `${Math.round(insights.timing_insights.avg_reorder_days)}d`
                                    : "-"}
                                </h2>
                              </div>
                            </div>
                          </div>

                          {/* CLV */}
                          <div className="card">
                            <div className='row w-100'>
                              <div className='col-12 pr-0 plpt'>
                                <h4 className="text font90x mb-0 text-white">CLV</h4>
                              </div>
                              <div className='col-12'>
                                <h2 className="mb-1 text-white mb-0">
                                  {insights.kpis?.estimated_clv
                                    ? Intl.NumberFormat().format(insights.kpis.estimated_clv)
                                    : 0}
                                </h2>
                              </div>
                            </div>
                          </div>

                        </div>
                      )}
                    </div>

                    {loading ? (
                      <div className="mt-2 mb-3">

                        {/* 🔷 TOP CARD (Opportunity Overview) */}
                        <div className="p-3 rounded-4 shadow-tile bg-white mb-3">

                          {/* Header */}
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <div className="skeleton-box" style={{
                              width: "160px",
                              height: "16px",
                              borderRadius: "6px"
                            }} />
                            <div className="skeleton-box" style={{
                              width: "120px",
                              height: "14px",
                              borderRadius: "6px"
                            }} />
                          </div>

                          {/* Value */}
                          <div className="skeleton-box mb-3" style={{
                            width: "140px",
                            height: "24px",
                            borderRadius: "6px"
                          }} />

                          {/* Progress bar */}
                          <div className="skeleton-box mb-3" style={{
                            width: "100%",
                            height: "8px",
                            borderRadius: "10px"
                          }} />

                          {/* Labels */}
                          <div className="skeleton-box mb-2" style={{
                            width: "120px",
                            height: "12px",
                            borderRadius: "6px"
                          }} />

                          <div className="skeleton-box mb-2" style={{
                            width: "80%",
                            height: "12px",
                            borderRadius: "6px"
                          }} />

                          {/* Footer */}
                          <div className="skeleton-box" style={{
                            width: "60%",
                            height: "10px",
                            borderRadius: "6px"
                          }} />

                        </div>

                        {/* 🔷 BOTTOM CARDS */}
                        <div className="row g-3">

                          {Array.from({ length: 2 }).map((_, i) => (
                            <div className="col-6" key={i}>
                              <div className="p-3 rounded-4 shadow-tile bg-white h-100">

                                {/* Icon + title */}
                                <div className="d-flex align-items-center gap-2 mb-2">
                                  <div className="skeleton-box" style={{
                                    width: "20px",
                                    height: "20px",
                                    borderRadius: "50%"
                                  }} />
                                  <div className="skeleton-box" style={{
                                    width: "80px",
                                    height: "14px",
                                    borderRadius: "6px"
                                  }} />
                                </div>

                                {/* Customers */}
                                <div className="skeleton-box mb-2" style={{
                                  width: "100px",
                                  height: "12px",
                                  borderRadius: "6px"
                                }} />

                                {/* Revenue */}
                                <div className="skeleton-box" style={{
                                  width: "120px",
                                  height: "16px",
                                  borderRadius: "6px"
                                }} />

                              </div>
                            </div>
                          ))}

                        </div>

                      </div>
                    ) : (
                      <InsightsDashboard insights={insights} website={shopwebsite} />
                    )}

                    {/* ========================= */}
                    {/* 🔗 SHOP LINK CARD */}
                    {/* ========================= */}
                    <div className="row g2">
                    <div className="col-12 mt-2">
                      <div className="card p-3 border-0 shadow-sm mb-0">
                        <div className="d-flex align-items-center mb-2">
                          <LinkIcon size={18} className="mr-2 text-dark" />
                          <h6 className="mb-0 font-bold">Online Shop Link</h6>
                        </div>

                        <small className="text-muted mb-2 d-block font8">
                          This link will be included as a CTA in messages
                        </small>

                        {/* INPUT GROUP */}
                        <div className="input-group mt-2">                          
                          <input
                            type="text"
                            className={`form-control font9 br10`}
                            value={shopwebsite}
                            placeholder="yourstore.com/page"
                            readOnly
                          />
                          <Link to="/seller/profile"><div className="input-group-text pb-2 bg-inherit border-0"><Edit className="text-primary"/></div></Link>
                        </div>

                        <small className="text-muted mt-4 d-block font8">
                          Tip: Keep it short and direct to checkout for higher conversion
                        </small>
                      </div>
                    </div>
                    </div>
              </div>
              <div className='col-lg-4 col-12'> 
                  {loading ? (
                    <div className="row g-3 mb-2">

                      <h5 className="font-bold">Customer Segments</h5>

                      {Array.from({ length: 4 }).map((_, i) => (
                        <div className="col-6 mt-1 mb-3" key={i}>
                          <div className="p-3 rounded-4 shadow-tile h-100 bg-white">

                            {/* Top row */}
                            <div className="d-flex align-items-center gap-2 mb-3">
                              <div className="skeleton-box" style={{
                                width: "32px",
                                height: "32px",
                                borderRadius: "10px"
                              }} />
                              <div className="skeleton-box" style={{
                                width: "90px",
                                height: "14px",
                                borderRadius: "6px"
                              }} />
                            </div>

                            {/* Value */}
                            <div className="skeleton-box mb-2" style={{
                              width: "70px",
                              height: "18px",
                              borderRadius: "6px"
                            }} />

                            {/* Footer */}
                            <div className="skeleton-box" style={{
                              width: "120px",
                              height: "12px",
                              borderRadius: "6px"
                            }} />

                          </div>
                        </div>
                      ))}

                    </div>
                  ) : (
                    <CustomerSegmentsGrid segments={segments} />
                  )}
                  
                  <div className="card f8 p-3">                                    
                      <p className="mb-0"><b>Purchase Frequency</b></p>
                      <div className="card-body p-1 pb-5 pt-4">
                          {ocharts.order_frequency && (
                              <PieChart
                                  chart_title=""
                                  chart_categories={ocharts.order_frequency.categories}
                                  chart_data={ocharts.order_frequency.data}
                              />
                          )}
                      </div>                      
                  </div>

              </div>              
              <div className='col-lg-4 col-12'>

                    <RetargetPerformanceCard data={stats}/>

                    <div className="card f8 p-3 mb-4">                                    
                      <p className="mb-0"><b>SMS by Status</b></p>
                      <div className="card-body p-1 pb-3 pt-3">
                          <DonutChart
                              chart_categories={charts.sms_stats?.categories || []}
                              chart_data={charts.sms_stats?.data || []}
                          />
                      </div>                      
                  </div>
                  <div className="card f8 p-3 hide">                                    
                      <p className="mb-0"><b>Rewards Issued (Months)</b></p>
                      <div className="card-body p-1 pb-3 pt-3">
                          <BarChart
                                chart_categories={charts.rewards_over_time?.categories || []}
                                chart_data={charts.rewards_over_time?.issued || []}
                            />
                      </div>                      
                  </div>

              </div>
            </div>
          </div>
        </div>
        
      </section>

      {/* Bulk SMS Modal */}
      <SMSBulkPopup
          show={showPopup}
          onClose={() => setShowPopup(false)}
          tasks={popupTasks}
          smslink={""} // or pass shop link
          onSuccess={() => {
            setShowPopup(false);
          }}
        />
    </>
  );
}


export default RetargetTask;
